import isNil from "lodash/isNil";
import { AccessLevel, Booking, BookingCategory, Id } from "PFTypes";
import { useCallback, useMemo } from "react";

import { useMyAccountInfo } from "./queries/account/use_my_account_info";

type CheckWriteAccessRowSpecificOptions = {
  demandRelated?: boolean;
  categoryIds: number[];
};

type UseBookingCategoriesReturn = {
  bookingCategories: BookingCategory[];
  getBookingCategory: (categoryId: Id | null | undefined) => BookingCategory | undefined;
  getCategory: (booking: Booking) => BookingCategory | undefined;
  mainCategory: BookingCategory | null;
  checkWriteAccessToAtLeastOneCategory: (options?: { demandRelated?: boolean }) => boolean;
  checkWriteAccessRowSpecific: (options: CheckWriteAccessRowSpecificOptions) => boolean;
};

const useBookingCategories = (): UseBookingCategoriesReturn => {
  const {
    data: { availability }
  } = useMyAccountInfo();

  const bookingCategories = useMemo<BookingCategory[]>(
    () => availability?.booking_categories || [],
    [availability?.booking_categories]
  );

  const getBookingCategory = useCallback<(categoryId: Id | null | undefined) => BookingCategory | undefined>(
    (categoryId) => bookingCategories.find(({ id }) => id === categoryId),
    [bookingCategories]
  );

  // eslint-disable-next-line func-call-spacing
  const checkWriteAccessToAtLeastOneCategory = useCallback<
    ({ demandRelated }: { demandRelated?: boolean }) => boolean
  >(
    ({ demandRelated }) =>
      bookingCategories
        .filter(({ activity_assignable: activityAssignable }) => {
          if (isNil(demandRelated)) {
            return true;
          }
          return demandRelated ? !!activityAssignable : !activityAssignable;
        })
        .some((i) => i.bookings_permissions === AccessLevel.ReadWrite && !i.archived),
    [bookingCategories]
  );

  const checkWriteAccessRowSpecific = useCallback<(options: CheckWriteAccessRowSpecificOptions) => boolean>(
    ({ demandRelated, categoryIds }) =>
      bookingCategories
        .filter(({ id }) => categoryIds.includes(id))
        .filter(({ activity_assignable: activityAssignable }) => {
          if (isNil(demandRelated)) {
            return true;
          }
          return demandRelated ? !!activityAssignable : !activityAssignable;
        })
        .some((i) => i.bookings_permissions === AccessLevel.ReadWrite && !i.archived),
    [bookingCategories]
  );

  const getCategory = useCallback<(booking: Booking) => BookingCategory | undefined>(
    ({ booking_category_id: categoryId }: Booking) => getBookingCategory(categoryId),
    [getBookingCategory]
  );

  const mainCategory = useMemo<BookingCategory | null>(
    () => bookingCategories.find(({ main }) => main) || null,
    [bookingCategories]
  );

  return {
    bookingCategories,
    getBookingCategory,
    getCategory,
    mainCategory,
    checkWriteAccessToAtLeastOneCategory,
    checkWriteAccessRowSpecific
  };
};

export default useBookingCategories;
